import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  VoiceState,
  ConversationMode,
  ENDPOINT,
  canTransition,
  makeSessionId,
  makeTurnId,
  isLive,
  shouldAutoRestartAfterKrishna,
} from "./voiceStateMachine";

/**
 * useKalkiVoice — the single authoritative voice conversation controller.
 *
 * Replaces the earlier scattered start/stop/auto-restart logic with ONE
 * state machine that owns:
 *   • Session lifecycle          (startTalking / endSession)
 *   • Microphone capture         (shared stream across turns; iOS single-flight)
 *   • Endpoint detection         (silence + min utterance; mid-sentence pauses OK)
 *   • STT submission             (one per committed utterance)
 *   • LLM response               (abortable; stale callbacks rejected)
 *   • TTS synthesis + playback   (one owner; completion = actual `ended`)
 *   • Interrupt / barge          (controlled: via explicit tap only)
 *   • Mode toggle                (Controlled vs Hands-Free — Controlled default)
 *
 * Invariants enforced here:
 *   1. USER_SPEAKING and KRISHNA_SPEAKING never active together.
 *   2. One active turn per session.
 *   3. One committed final transcript per utterance.
 *   4. One AI request per committed turn.
 *   5. One active playback owner.
 *   6. Stale async callbacks (older session/turn) cannot mutate state.
 *   7. In Controlled mode, listening never auto-restarts after Krishna.
 *   8. Krishna playback completes before any transition to READY.
 */

export { VoiceState, ConversationMode };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const apiAxios = axios.create({ baseURL: API, withCredentials: true });

const log = (stage, ...rest) => {
  try { console.info(`[KALKI VOICE] ${stage}`, ...rest); } catch (_) {}
};
const logErr = (stage, ...rest) => {
  try { console.warn(`[KALKI VOICE ERROR] ${stage}`, ...rest); } catch (_) {}
};

export function useKalkiVoice({ conversationId, audioOn = true } = {}) {
  // ---------- Public reactive state ----------
  const [voiceState, setVoiceState] = useState(VoiceState.IDLE);
  const [transcript, setTranscript] = useState("");
  const [kalkiText, setKalkiText] = useState("");
  const [experience, setExperience] = useState(null);
  const [vedicSky, setVedicSky] = useState(null);
  const [amplitude, setAmplitude] = useState(0);
  const [micLevel, setMicLevel] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [canReplay, setCanReplay] = useState(false);
  const [conversationMode, _setConversationMode] = useState(ConversationMode.CONTROLLED);

  // ---------- Session + turn tokens — the gate against stale callbacks ----------
  const sessionIdRef = useRef(null);
  const turnIndexRef = useRef(0);
  const liveTurnIdRef = useRef(null);
  const abortCtrlRef = useRef(null);                 // in-flight API aborter
  const audioOnRef = useRef(audioOn);
  const modeRef = useRef(conversationMode);
  useEffect(() => { audioOnRef.current = audioOn; }, [audioOn]);
  useEffect(() => { modeRef.current = conversationMode; }, [conversationMode]);

  // ---------- Platform capability probes ----------
  const supportsSpeech = typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const isIOS = typeof window !== "undefined"
    && /iPad|iPhone|iPod/.test(navigator.userAgent)
    && !window.MSStream;
  const supportsMediaRecorder = typeof window !== "undefined"
    && !!window.MediaRecorder
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  const effectiveSupportsSpeech = (isIOS && supportsMediaRecorder) || supportsSpeech;

  // ---------- Audio playback element (persistent across turns) ----------
  const audioElRef = useRef(null);
  const lastBlobUrlRef = useRef(null);
  const lastAudioBlobRef = useRef(null);
  const unlockedRef = useRef(false);
  const settleTimerRef = useRef(null);

  // ---------- Mic capture refs ----------
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recognitionRef = useRef(null);
  const micInitPromiseRef = useRef(null);
  const vadCtxRef = useRef(null);
  const vadAnalyserRef = useRef(null);
  const vadRafRef = useRef(null);
  const utteranceStartRef = useRef(0);
  const lastSpeechAtRef = useRef(0);
  const sawSpeechRef = useRef(false);
  const utteranceCapRef = useRef(null);

  // ---------- Visual amplitude RAF (for Krishna speaking rings) ----------
  const ampRafRef = useRef(null);

  // ---------- Live state ref (synchronous reads inside callbacks) ----------
  const stateRef = useRef(VoiceState.IDLE);

  // ----- One safe transition entry point -----
  const transitionTo = useCallback((next, { force = false } = {}) => {
    const curr = stateRef.current;
    if (curr === next) return true;
    if (!force && !canTransition(curr, next)) {
      logErr("ILLEGAL_TRANSITION", curr, "→", next);
      return false;
    }
    stateRef.current = next;
    setVoiceState(next);
    log("STATE", curr, "→", next);
    return true;
  }, []);

  // ---------------------------------------------------------------
  //                       AMPLITUDE (visual rings)
  // ---------------------------------------------------------------
  const stopAmp = useCallback(() => {
    if (ampRafRef.current) cancelAnimationFrame(ampRafRef.current);
    ampRafRef.current = null;
    setAmplitude(0);
  }, []);
  const startAmp = useCallback(() => {
    stopAmp();
    const started = performance.now();
    const tick = () => {
      const t = (performance.now() - started) / 1000;
      const base = 0.35 + 0.25 * Math.sin(t * 6.2);
      const jitter = 0.15 * Math.sin(t * 17 + Math.random());
      setAmplitude(Math.max(0.1, Math.min(0.95, base + jitter)));
      ampRafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, [stopAmp]);

  // ---------------------------------------------------------------
  //                 AUDIO ELEMENT (persistent, one owner)
  // ---------------------------------------------------------------
  const ensureAudioElement = useCallback(() => {
    if (audioElRef.current) return audioElRef.current;
    const el = new Audio();
    el.preload = "auto";
    el.autoplay = false;
    el.muted = false;
    el.volume = 1;
    try { el.playsInline = true; } catch (_) {}
    el.addEventListener("playing", () => {
      // Only accept this event if the owning turn is still live.
      const owner = el.dataset.turnId;
      const owningSession = el.dataset.sessionId;
      if (!isLive(owningSession, owner, sessionIdRef.current, liveTurnIdRef.current)) {
        logErr("STALE_PLAYING_IGNORED", owner, "live=", liveTurnIdRef.current);
        try { el.pause(); } catch (_) {}
        return;
      }
      transitionTo(VoiceState.KRISHNA_SPEAKING);
      startAmp();
    });
    el.addEventListener("ended", () => {
      stopAmp();
      const owner = el.dataset.turnId;
      const owningSession = el.dataset.sessionId;
      if (!isLive(owningSession, owner, sessionIdRef.current, liveTurnIdRef.current)) {
        logErr("STALE_ENDED_IGNORED", owner);
        return;
      }
      // Revoke the blob URL to free memory.
      if (lastBlobUrlRef.current) {
        try { URL.revokeObjectURL(lastBlobUrlRef.current); } catch (_) {}
        lastBlobUrlRef.current = null;
      }
      // Post-playback settle before READY. In Controlled mode we stop here
      // and wait for the user to tap SPEAK. In Hands-Free we auto-restart.
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(() => {
        // Confirm nothing changed during the settle window.
        if (stateRef.current !== VoiceState.KRISHNA_SPEAKING) return;
        transitionTo(VoiceState.READY);
        if (shouldAutoRestartAfterKrishna(modeRef.current)) {
          // Hands-free: user opted in to continuous conversation.
          beginNextTurnRef.current?.();
        }
      }, ENDPOINT.POST_PLAYBACK_SETTLE_MS);
    });
    el.addEventListener("error", () => {
      logErr("AUDIO_ERROR", el.error?.code, el.error?.message);
    });
    audioElRef.current = el;
    return el;
  }, [transitionTo, startAmp, stopAmp]);

  // Unlock audio inside a user gesture — must be called from startTalking.
  const unlockAudio = useCallback(async () => {
    const el = ensureAudioElement();
    try {
      el.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=";
      const p = el.play();
      if (p && typeof p.then === "function") {
        await p.then(() => {
          el.pause();
          try { el.currentTime = 0; } catch (_) {}
        }).catch(() => {});
      }
      unlockedRef.current = true;
      log("AUDIO_UNLOCKED");
    } catch (e) {
      logErr("AUDIO_UNLOCK_FAIL", e?.name || e);
    }
  }, [ensureAudioElement]);

  // ---------------------------------------------------------------
  //                   MICROPHONE (shared across turns)
  // ---------------------------------------------------------------
  const acquireMic = useCallback(async () => {
    const existing = mediaStreamRef.current;
    if (existing && existing.getAudioTracks().some((t) => t.readyState === "live")) {
      return existing;
    }
    if (micInitPromiseRef.current) return micInitPromiseRef.current;
    micInitPromiseRef.current = navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    try {
      const stream = await micInitPromiseRef.current;
      mediaStreamRef.current = stream;
      return stream;
    } finally {
      micInitPromiseRef.current = null;
    }
  }, []);

  const releaseMic = useCallback(() => {
    try {
      if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
      vadRafRef.current = null;
      const ctx = vadCtxRef.current;
      if (ctx && ctx.state !== "closed") try { ctx.close(); } catch (_) {}
      vadCtxRef.current = null;
      vadAnalyserRef.current = null;
      const stream = mediaStreamRef.current;
      if (stream) stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
      mediaStreamRef.current = null;
      setMicLevel(0);
    } catch (_) {}
  }, []);

  // ---------------------------------------------------------------
  //      VAD — endpoint detection shared by iOS and Chrome paths
  // ---------------------------------------------------------------
  const startVAD = useCallback((stream, { onFinalize, onBargeOffer } = {}) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = vadCtxRef.current || new AC();
      vadCtxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      vadAnalyserRef.current = analyser;

      const buf = new Uint8Array(analyser.frequencyBinCount);
      utteranceStartRef.current = performance.now();
      lastSpeechAtRef.current = performance.now();
      sawSpeechRef.current = false;

      // Cap safety — never let an utterance exceed MAX_UTTERANCE_MS
      if (utteranceCapRef.current) clearTimeout(utteranceCapRef.current);
      utteranceCapRef.current = setTimeout(() => {
        log("VAD_MAX_UTTERANCE_HIT");
        onFinalize?.();
      }, ENDPOINT.MAX_UTTERANCE_MS);

      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let min = 255, max = 0;
        for (let i = 0; i < buf.length; i++) {
          if (buf[i] < min) min = buf[i];
          if (buf[i] > max) max = buf[i];
        }
        const level = Math.max(0, Math.min(1, (max - min) / 255));
        setMicLevel(level);
        const now = performance.now();
        if (level > ENDPOINT.VAD_LEVEL_THRESHOLD) {
          lastSpeechAtRef.current = now;
          if (!sawSpeechRef.current && (now - utteranceStartRef.current) >= ENDPOINT.SPEECH_ONSET_MS) {
            sawSpeechRef.current = true;
            log("VAD_SPEECH_ONSET");
          }
        } else {
          // Only consider silence-based finalization AFTER we heard speech,
          // and only after END_OF_SPEECH_SILENCE_MS of continuous quiet.
          const silence = now - lastSpeechAtRef.current;
          const elapsed = now - utteranceStartRef.current;
          if (sawSpeechRef.current
              && elapsed >= ENDPOINT.MIN_UTTERANCE_MS
              && silence >= ENDPOINT.END_OF_SPEECH_SILENCE_MS) {
            log("VAD_ENDPOINT", `silence=${silence|0}ms elapsed=${elapsed|0}ms`);
            onFinalize?.();
            return; // stop the loop; cleanup happens in stopVAD
          }
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      logErr("VAD_START_FAIL", e?.message || e);
    }
  }, []);

  const stopVAD = useCallback(() => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    vadRafRef.current = null;
    if (utteranceCapRef.current) clearTimeout(utteranceCapRef.current);
    utteranceCapRef.current = null;
  }, []);

  // ---------------------------------------------------------------
  //                 TURN LIFECYCLE (serial coordinator)
  // ---------------------------------------------------------------
  const beginNextTurnRef = useRef(null);
  const finalizeTurnRef = useRef(null);
  // Voice 6.0 — STT finalisation guard timer (see finalizeTurn).
  const finalizeGuardRef = useRef(null);

  // Capture a single utterance → STT → runLLM → runTTS → play → settle.
  const runPipeline = useCallback(async (utteranceText, mySession, myTurn) => {
    if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) {
      log("PIPELINE_STALE_DROPPED");
      return;
    }
    if (!transitionTo(VoiceState.KRISHNA_THINKING)) return;

    // Cancel any older in-flight request before opening a new one.
    try { abortCtrlRef.current?.abort(); } catch (_) {}
    const ctl = new AbortController();
    abortCtrlRef.current = ctl;

    // ------------------------------------------------------------------
    // Audio-OFF path (text-only) — one-shot /respond, no streaming TTS.
    // ------------------------------------------------------------------
    if (!audioOnRef.current) {
      try {
        const r = await apiAxios.post("/kalki/respond",
          { text: utteranceText, conversationId: conversationId || "talk_default", turnId: myTurn, mode: "voice" },
          { signal: ctl.signal });
        if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
        const reply = r.data?.reply || "";
        setKalkiText(reply);
        setExperience(r.data?.suggested_experience || null);
        setVedicSky(r.data?.vedic_sky || null);
        transitionTo(VoiceState.READY);
      } catch (e) {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
        logErr("AI_FAIL", e?.response?.status, e?.message);
        setErrorMsg("KALKI could not respond right now. Please try again.");
        transitionTo(VoiceState.ERROR);
      }
      return;
    }

    // ------------------------------------------------------------------
    // Voice 6.0 — Streaming TTS path.
    //
    // Replaces the previous one-shot `/respond` + `/kalki/tts` sequence
    // with SSE consumption of `/respond/stream` + per-sentence TTS fan-
    // out. First audible word now lands as soon as the first full
    // sentence streams back — typically under 2s on a stable network vs
    // the old 5-6s end-to-end wait.
    //
    // Telemetry (printed as `[voice6.perf]` lines so Playwright asserts
    // on them later): release → first_text_chunk → first_audio_blob →
    // first_playing.
    // ------------------------------------------------------------------
    const t0 = performance.now();
    let firstTextAt = null;
    let firstAudioAt = null;
    const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/kalki/respond/stream`;
    const base = apiAxios.defaults.baseURL || "";
    void base;
    let streamResponse;
    try {
      streamResponse = await fetch(streamUrl, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({
          text: utteranceText,
          conversationId: conversationId || "talk_default",
          turnId: myTurn, mode: "voice",
        }),
        signal: ctl.signal,
      });
      if (!streamResponse.ok) throw new Error(`stream_http_${streamResponse.status}`);
    } catch (e) {
      if (e?.name === "AbortError") return;
      logErr("STREAM_FAIL", e?.message);
      setErrorMsg("KALKI could not respond right now. Please try again.");
      transitionTo(VoiceState.ERROR);
      return;
    }

    // Chunked playback queue — one HTMLAudioElement, FIFO sentences.
    const audioQueueRef = { items: [], playing: false };
    const el = ensureAudioElement();
    el.dataset.sessionId = mySession;
    el.dataset.turnId = myTurn;
    const playNext = async () => {
      if (audioQueueRef.playing) return;
      const next = audioQueueRef.items.shift();
      if (!next) return;
      if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
      audioQueueRef.playing = true;
      const url = URL.createObjectURL(next);
      el.src = url;
      el.muted = false; el.volume = 1;
      el.onended = () => {
        try { URL.revokeObjectURL(url); } catch (_) {}
        audioQueueRef.playing = false;
        if (audioQueueRef.items.length) playNext();
      };
      try {
        await el.play();
        if (firstAudioAt === null) {
          firstAudioAt = performance.now();
          try { log(`[voice6.perf] first_playing=${(firstAudioAt - t0).toFixed(0)}ms t2t=${(firstAudioAt - (firstTextAt || t0)).toFixed(0)}ms`); } catch (_) {}
        }
      } catch (e) {
        audioQueueRef.playing = false;
        logErr("PLAY_FAIL", e?.name || e);
      }
    };

    const requestTTSForSegment = async (segText) => {
      if (!segText.trim()) return;
      try {
        const r = await apiAxios.post("/kalki/tts",
          { text: segText, conversationId, turnId: `${myTurn}_${audioQueueRef.items.length}` },
          { responseType: "blob", signal: ctl.signal });
        if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
        const type = r.headers?.["content-type"] || r.data?.type || "";
        if (!type.startsWith("audio/")) return;
        audioQueueRef.items.push(r.data);
        if (!audioQueueRef.playing) playNext();
      } catch (e) {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
        logErr("TTS_SEGMENT_FAIL", e?.message);
      }
    };

    // Consume the SSE body progressively.
    const reader = streamResponse.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let sseBuf = "";
    let sentenceBuf = "";
    let fullReply = "";
    const sentenceRe = /([.!?।])\s/;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, {stream: true});
        const events = sseBuf.split("\n\n");
        sseBuf = events.pop() || "";
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch (_) { continue; }
          if (payload.done) {
            if (sentenceBuf.trim()) await requestTTSForSegment(sentenceBuf.trim());
            fullReply = payload.reply || fullReply || sentenceBuf;
            setKalkiText(fullReply);
            setExperience(payload.suggested_experience || null);
            setVedicSky(payload.vedic_sky || null);
            try {
              if (payload.blessing?.ok) window.dispatchEvent(new CustomEvent("kalki:blessing-granted", {detail: payload.blessing}));
              if (payload.character_milestone?.message) window.dispatchEvent(new CustomEvent("kalki:character-milestone", {detail: payload.character_milestone}));
            } catch (_) {}
            continue;
          }
          const token = payload.delta || payload.text || "";
          if (!token) continue;
          if (firstTextAt === null) {
            firstTextAt = performance.now();
            try { log(`[voice6.perf] first_text=${(firstTextAt - t0).toFixed(0)}ms`); } catch (_) {}
          }
          sentenceBuf += token;
          fullReply += token;
          setKalkiText(fullReply);
          // Flush completed sentences into TTS queue.
          let m;
          while ((m = sentenceBuf.match(sentenceRe)) !== null) {
            const cut = m.index + m[0].length;
            const seg = sentenceBuf.slice(0, cut).trim();
            sentenceBuf = sentenceBuf.slice(cut);
            if (seg) await requestTTSForSegment(seg);
          }
        }
      }
      // Final partial segment.
      if (sentenceBuf.trim()) await requestTTSForSegment(sentenceBuf.trim());
    } catch (e) {
      if (e?.name === "AbortError") return;
      logErr("STREAM_READ_FAIL", e?.message);
      setErrorMsg("KALKI could not respond right now. Please try again.");
      transitionTo(VoiceState.ERROR);
      return;
    }
    // Pipeline finished — the 'ended' handler on the <audio> element will
    // eventually drain the queue and the state machine moves to READY from
    // elsewhere once playback truly settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, transitionTo, ensureAudioElement]);

  const playAudio = useCallback(async (blob, mySession, myTurn) => {
    const el = ensureAudioElement();
    lastAudioBlobRef.current = blob;
    setCanReplay(true);
    if (lastBlobUrlRef.current) {
      try { URL.revokeObjectURL(lastBlobUrlRef.current); } catch (_) {}
    }
    const url = URL.createObjectURL(blob);
    lastBlobUrlRef.current = url;
    el.dataset.sessionId = mySession;
    el.dataset.turnId = myTurn;
    el.src = url;
    el.muted = false;
    el.volume = 1;
    try {
      await el.play();
      // KRISHNA_SPEAKING is set inside the 'playing' listener once native
      // playback actually begins — not before.
    } catch (e) {
      if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
      logErr("PLAY_FAIL", e?.name || e);
      setErrorMsg("Your browser needs one tap to enable KALKI's voice.");
      transitionTo(VoiceState.ERROR);
    }
  }, [ensureAudioElement, transitionTo]);

  // ----- Chrome / Android: webkitSpeechRecognition with pause tolerance -----
  const startListeningSR = useCallback((mySession, myTurn) => {
    try { recognitionRef.current?.stop(); } catch (_) {}
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US";
    rec.continuous = true;         // we drive our own endpoint detector
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;

    let aggregated = "";

    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) aggregated += r[0].transcript;
        else interim += r[0].transcript;
      }
      const t = (aggregated + " " + interim).trim();
      setTranscript(t);
      // Any audio detected resets the silence clock for our VAD-less path.
      lastSpeechAtRef.current = performance.now();
      sawSpeechRef.current = true;
    };
    rec.onerror = (ev) => {
      if (ev.error === "no-speech" || ev.error === "aborted") return;
      logErr("SR_ERROR", ev.error);
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setErrorMsg("Please allow microphone access to speak to KALKI.");
        if (isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) {
          transitionTo(VoiceState.ERROR);
        }
      }
    };
    rec.onend = async () => {
      // onend fires whenever Chrome's recogniser decides it's done — we may
      // have called stop() ourselves (finalize) or Chrome auto-stopped. Either
      // way, this is where we commit if we are still the live turn.
      if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
      const text = (aggregated || "").trim();
      if (!sawSpeechRef.current || !text) {
        // Nothing heard — remain READY, don't submit an empty question.
        log("SR_END_NO_SPEECH");
        setTranscript("");
        transitionTo(VoiceState.READY);
        return;
      }
      transitionTo(VoiceState.USER_TURN_FINALIZING);
      transitionTo(VoiceState.TRANSCRIBING);
      // Chrome already produced the transcript, so TRANSCRIBING is a brief
      // moment then straight into the AI pipeline.
      runPipeline(text, mySession, myTurn);
    };

    aggregated = "";
    setTranscript("");
    try { rec.start(); } catch (e) { logErr("SR_START_FAIL", e?.name || e); }
  }, [runPipeline, transitionTo]);

  // ----- iOS Safari: MediaRecorder + Whisper STT via /api/kalki/stt -----
  const _pickIOSMime = () => {
    const candidates = ["audio/mp4", "audio/mp4;codecs=mp4a.40.2", "audio/aac", "audio/webm;codecs=opus", "audio/webm"];
    for (const m of candidates) {
      try { if (window.MediaRecorder?.isTypeSupported?.(m)) return m; } catch (_) {}
    }
    return "";
  };

  const startListeningIOS = useCallback(async (mySession, myTurn, stream) => {
    try {
      // Audit (P0 2026-02): defensively stop any prior MediaRecorder before
      // creating a new one so a superseded turn cannot leave a second
      // encoder holding the mic.
      try {
        const prev = mediaRecorderRef.current;
        if (prev && prev.state !== "inactive") prev.stop();
      } catch (_) {}
      const mime = _pickIOSMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (ev) => { if (ev.data?.size) chunks.push(ev.data); };
      rec.onstop = async () => {
        if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) {
          log("IOS_STOP_STALE");
          return;
        }
        if (!sawSpeechRef.current || !chunks.length) {
          log("IOS_STOP_NO_SPEECH");
          setTranscript("");
          transitionTo(VoiceState.READY);
          return;
        }
        const blob = new Blob(chunks, { type: rec.mimeType || mime || "audio/mp4" });
        if (blob.size < 1024) {
          log("IOS_STOP_BLOB_TINY", blob.size);
          transitionTo(VoiceState.READY);
          return;
        }
        if (!transitionTo(VoiceState.USER_TURN_FINALIZING)) return;
        if (!transitionTo(VoiceState.TRANSCRIBING)) return;
        try {
          try { abortCtrlRef.current?.abort(); } catch (_) {}
          const ctl = new AbortController();
          abortCtrlRef.current = ctl;
          const ext = (rec.mimeType || mime || "").includes("webm") ? "webm" : "m4a";
          const fd = new FormData();
          fd.append("audio", blob, `clip.${ext}`);
          const resp = await fetch(`${BACKEND_URL}/api/kalki/stt`, {
            method: "POST", credentials: "include", body: fd, signal: ctl.signal,
          });
          if (!resp.ok) throw new Error(`STT HTTP ${resp.status}`);
          const data = await resp.json();
          const text = (data?.text || "").trim();
          if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
          if (!text) {
            setErrorMsg("I couldn't hear that clearly. Tap SPEAK to try again.");
            transitionTo(VoiceState.READY);
            return;
          }
          setTranscript(text);
          runPipeline(text, mySession, myTurn);
        } catch (e) {
          if (e?.name === "AbortError") return;
          if (!isLive(mySession, myTurn, sessionIdRef.current, liveTurnIdRef.current)) return;
          logErr("IOS_STT_FAIL", e?.name || e?.message);
          setErrorMsg("Voice is reconnecting. Please try again.");
          transitionTo(VoiceState.READY);
        }
      };
      rec.start();
      log("IOS_MIC_STARTED", mime);
    } catch (e) {
      logErr("IOS_MIC_START_FAIL", e?.name || e?.message);
      if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
        setErrorMsg("Please allow microphone access in Settings → Safari.");
      } else {
        setErrorMsg("I could not reach the microphone. Please try again.");
      }
      transitionTo(VoiceState.ERROR);
    }
  }, [runPipeline, transitionTo]);

  // ----- beginNextTurn: shared entry for both platforms -----
  const beginNextTurn = useCallback(async () => {
    // Guard: never start a new turn if we aren't READY (or just finished).
    const curr = stateRef.current;
    if (curr !== VoiceState.READY && curr !== VoiceState.INTERRUPTED) {
      log("BEGIN_TURN_REJECTED", curr);
      return;
    }
    if (!sessionIdRef.current) {
      logErr("BEGIN_TURN_NO_SESSION");
      return;
    }
    turnIndexRef.current += 1;
    const myTurn = makeTurnId(sessionIdRef.current, turnIndexRef.current);
    liveTurnIdRef.current = myTurn;
    const mySession = sessionIdRef.current;
    setTranscript("");
    setErrorMsg("");
    sawSpeechRef.current = false;

    if (!transitionTo(VoiceState.USER_SPEAKING)) return;

    try {
      const stream = await acquireMic();
      const track = stream.getAudioTracks()[0];
      if (!track || track.readyState !== "live") {
        setErrorMsg("Microphone didn't start — tap SPEAK again.");
        transitionTo(VoiceState.ERROR);
        return;
      }
      // VAD runs for BOTH paths; it's what finalizes the turn on silence.
      startVAD(stream, {
        onFinalize: () => {
          finalizeTurnRef.current?.();
        },
      });
      if (isIOS) {
        await startListeningIOS(mySession, myTurn, stream);
      } else if (supportsSpeech) {
        startListeningSR(mySession, myTurn);
      } else {
        setErrorMsg("This browser does not support voice input. Try Chrome.");
        transitionTo(VoiceState.ERROR);
      }
    } catch (e) {
      logErr("BEGIN_TURN_FAIL", e?.name || e?.message);
      if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
        setErrorMsg("Please allow microphone access to speak to KALKI.");
      } else {
        setErrorMsg("I could not reach the microphone. Please try again.");
      }
      transitionTo(VoiceState.ERROR);
    }
  }, [acquireMic, isIOS, supportsSpeech, startListeningIOS, startListeningSR, startVAD, transitionTo]);
  beginNextTurnRef.current = beginNextTurn;

  // Manually finalize the current utterance (user tapped DONE, or VAD said so)
  const finalizeTurn = useCallback(() => {
    const curr = stateRef.current;
    if (curr !== VoiceState.USER_SPEAKING) return;
    stopVAD();
    // Chrome: calling stop() causes `onend` to fire, where we commit.
    try { recognitionRef.current?.stop(); } catch (_) {}
    // iOS: stopping the recorder triggers `onstop` which commits.
    try {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state === "recording") rec.stop();
    } catch (_) {}
    // Voice 6.0 — STT finalisation guard. If recognizer.onend / recorder.onstop
    // never fires (STT provider stall, broken WebSocket, iOS background), we
    // force-advance to TRANSCRIBING after 700ms using whatever confirmed
    // transcript we already have. Prevents the stuck-mic "I pressed release
    // and nothing happened" complaint.
    if (finalizeGuardRef.current) clearTimeout(finalizeGuardRef.current);
    finalizeGuardRef.current = setTimeout(() => {
      const now = stateRef.current;
      if (now !== VoiceState.USER_SPEAKING
          && now !== VoiceState.USER_TURN_FINALIZING
          && now !== VoiceState.TRANSCRIBING) return;
      try { logErr("STT_FINALIZE_GUARD_FIRED state=", now); } catch (_) {}
      // Force-advance so submit can proceed.
      transitionTo(VoiceState.USER_TURN_FINALIZING, {force: true});
      transitionTo(VoiceState.TRANSCRIBING, {force: true});
    }, 700);
  }, [stopVAD, transitionTo]);
  finalizeTurnRef.current = finalizeTurn;

  // ---------------------------------------------------------------
  //                       SESSION LIFECYCLE
  // ---------------------------------------------------------------
  const startTalking = useCallback(async () => {
    if (stateRef.current !== VoiceState.IDLE
        && stateRef.current !== VoiceState.ERROR
        && stateRef.current !== VoiceState.ENDING) {
      log("START_IGNORED_STATE", stateRef.current);
      return;
    }
    // Fresh session
    sessionIdRef.current = makeSessionId();
    turnIndexRef.current = 0;
    liveTurnIdRef.current = null;
    setErrorMsg("");
    setTranscript("");
    setKalkiText("");
    setCanReplay(false);
    transitionTo(VoiceState.CONNECTING);
    try {
      await unlockAudio();
      // Prime mic ownership for the session so Safari doesn't re-prompt.
      await acquireMic();
      transitionTo(VoiceState.READY);
    } catch (e) {
      logErr("START_FAIL", e?.name || e?.message);
      setErrorMsg("I could not reach the microphone. Please try again.");
      transitionTo(VoiceState.ERROR);
      return;
    }
    // After CONNECTING→READY, immediately begin the first user turn so the
    // user can simply speak after tapping CALL once (the brief's happy path).
    beginNextTurn();
  }, [acquireMic, beginNextTurn, transitionTo, unlockAudio]);

  // Barge-in: user tapped INTERRUPT while Krishna is speaking OR while his
  // TTS audio is still being prepared.  P0 audit (2026-02): the old guard
  // only accepted KRISHNA_SPEAKING, which meant a tap during the prep
  // window (KRISHNA_THINKING / PREPARING_AUDIO) silently did nothing — the
  // in-flight fetch would resolve a moment later and play Krishna's old
  // reply ON TOP of the user's new recording. We now cancel BOTH phases.
  const barge = useCallback(() => {
    const s = stateRef.current;
    if (s !== VoiceState.KRISHNA_SPEAKING && s !== VoiceState.KRISHNA_THINKING) return;
    try { audioElRef.current?.pause(); } catch (_) {}
    // Mark the audio element as orphaned so a late `onplaying` from an
    // already-pending blob URL cannot flip us back into SPEAKING.
    try {
      const el = audioElRef.current;
      if (el) {
        el.dataset.owner = "orphan";
        el.dataset.sessionId = "orphan";
      }
    } catch (_) {}
    if (settleTimerRef.current) { clearTimeout(settleTimerRef.current); settleTimerRef.current = null; }
    try { abortCtrlRef.current?.abort(); } catch (_) {}
    stopAmp();
    transitionTo(VoiceState.INTERRUPTED);
    if (shouldAutoRestartAfterKrishna(modeRef.current)) {
      transitionTo(VoiceState.READY);
      beginNextTurn();
    } else {
      transitionTo(VoiceState.READY);
    }
  }, [beginNextTurn, stopAmp, transitionTo]);

  const endSession = useCallback(() => {
    // Hard-stop everything. Mark the live turn invalid so stale callbacks
    // from this session cannot mutate the next session.
    liveTurnIdRef.current = null;
    sessionIdRef.current = null;
    try { abortCtrlRef.current?.abort(); } catch (_) {}
    try { recognitionRef.current?.stop(); } catch (_) {}
    try {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state === "recording") rec.stop();
    } catch (_) {}
    mediaRecorderRef.current = null;
    if (settleTimerRef.current) { clearTimeout(settleTimerRef.current); settleTimerRef.current = null; }
    stopVAD();
    const el = audioElRef.current;
    if (el) {
      try { el.pause(); } catch (_) {}
      try { el.removeAttribute("src"); el.load?.(); } catch (_) {}
      try { delete el.dataset.sessionId; delete el.dataset.turnId; } catch (_) {}
    }
    if (lastBlobUrlRef.current) {
      try { URL.revokeObjectURL(lastBlobUrlRef.current); } catch (_) {}
      lastBlobUrlRef.current = null;
    }
    stopAmp();
    releaseMic();
    transitionTo(VoiceState.ENDING, { force: true });
    transitionTo(VoiceState.IDLE, { force: true });
    setTranscript("");
    setKalkiText("");
    setExperience(null);
    setVedicSky(null);
    setErrorMsg("");
    setCanReplay(false);
  }, [releaseMic, stopAmp, stopVAD, transitionTo]);

  // ---------------------------------------------------------------
  //                      PUBLIC HELPER METHODS
  // ---------------------------------------------------------------
  const unlockAudioOnly = useCallback(async () => { await unlockAudio(); }, [unlockAudio]);

  const replayLast = useCallback(async () => {
    const blob = lastAudioBlobRef.current;
    if (!blob || !sessionIdRef.current) return;
    const myTurn = liveTurnIdRef.current || makeTurnId(sessionIdRef.current, turnIndexRef.current);
    try { recognitionRef.current?.stop(); } catch (_) {}
    setErrorMsg("");
    await playAudio(blob, sessionIdRef.current, myTurn);
  }, [playAudio]);

  const retryPlayback = useCallback(async () => {
    const blob = lastAudioBlobRef.current;
    if (!blob) return;
    setErrorMsg("");
    const mySession = sessionIdRef.current;
    const myTurn = liveTurnIdRef.current;
    if (!mySession || !myTurn) return;
    await playAudio(blob, mySession, myTurn);
  }, [playAudio]);

  const speakText = useCallback(async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    const mySession = sessionIdRef.current || makeSessionId();
    sessionIdRef.current = mySession;
    const myTurn = `direct_${Date.now().toString(36)}`;
    liveTurnIdRef.current = myTurn;
    transitionTo(VoiceState.KRISHNA_THINKING, { force: true });
    try {
      const r = await apiAxios.post("/kalki/tts",
        { text: t, conversationId, turnId: myTurn },
        { responseType: "blob" });
      const type = r.headers?.["content-type"] || r.data?.type || "";
      if (!type.startsWith("audio/")) throw new Error(`bad_content_type:${type}`);
      if (!r.data || r.data.size === 0) throw new Error("empty_blob");
      await playAudio(r.data, mySession, myTurn);
    } catch (e) {
      logErr("DIRECT_TTS_FAIL", e?.response?.status, e?.message);
      setErrorMsg("Voice couldn't play. Please try again.");
      transitionTo(VoiceState.ERROR, { force: true });
    }
  }, [conversationId, playAudio, transitionTo]);

  // iOS-specific helpers kept for compatibility with TalkDiagnostics.jsx.
  const stopListeningIOS = useCallback(() => {
    try {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state === "recording") rec.stop();
    } catch (_) {}
  }, []);
  const releaseMicIOS = useCallback(() => { releaseMic(); }, [releaseMic]);

  // Public conversation mode setter — persisted only in state (not on server).
  const setConversationMode = useCallback((m) => {
    if (m !== ConversationMode.CONTROLLED && m !== ConversationMode.HANDS_FREE) return;
    _setConversationMode(m);
    log("MODE_SET", m);
  }, []);

  // ---------- Legacy aliases (used by existing UI code) ----------
  // Old code calls `stopListening()` — map it to finalizeTurn.
  const stopListening = finalizeTurn;

  // Cleanup on unmount — do not touch an in-flight session on StrictMode double-mount
  useEffect(() => {
    return () => {
      // Only release ambient resources — never cancel a live session merely
      // because React re-renders. Full teardown goes through endSession().
      stopAmp();
    };
  }, [stopAmp]);

  return useMemo(() => ({
    // reactive
    voiceState,
    transcript,
    kalkiText,
    experience,
    vedicSky,
    amplitude,
    micLevel,
    errorMsg,
    canReplay,
    conversationMode,
    supportsSpeech: effectiveSupportsSpeech,
    isIOS,

    // controller API
    startTalking,
    beginNextTurn,
    finalizeTurn,
    stopListening,        // legacy alias
    retryPlayback,
    replayLast,
    speakText,
    unlockAudioOnly,
    barge,
    endSession,
    setConversationMode,

    // diagnostic / compat
    stopListeningIOS,
    releaseMicIOS,
  }), [
    voiceState, transcript, kalkiText, experience, vedicSky, amplitude,
    micLevel, errorMsg, canReplay, conversationMode, effectiveSupportsSpeech, isIOS,
    startTalking, beginNextTurn, finalizeTurn, stopListening, retryPlayback,
    replayLast, speakText, unlockAudioOnly, barge, endSession,
    setConversationMode, stopListeningIOS, releaseMicIOS,
  ]);
}
